import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Play, MapPin, Compass } from "@phosphor-icons/react";

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [teeIdx, setTeeIdx] = useState(1); // default to "Blue"
  useEffect(() => { api.get(`/courses/${id}`).then((r) => setC(r.data)); }, [id]);
  if (!c) return <div className="py-10">Loading…</div>;
  const tee = c.tees[teeIdx] || c.tees[0];
  const front = c.holes.slice(0, 9);
  const back = c.holes.slice(9);
  const sumYards = (start, end) => tee.yards.slice(start, end).reduce((a, b) => a + b, 0);
  const sumPar = (arr) => arr.reduce((a, b) => a + b.par, 0);
  return (
    <div className="space-y-6">
      <div className="relative h-56 md:h-72 rounded-2xl overflow-hidden">
        {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2537]/95 via-[#0F2537]/40 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 p-6 text-white">
          <div className="flex items-center gap-1 text-xs font-semibold text-white/80"><MapPin size={12} weight="fill" />{c.location}</div>
          <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter">{c.name}</h1>
          <p className="text-white/80 text-sm mt-2 max-w-2xl">{c.description}</p>
        </div>
      </div>

      <div className="card-default p-5">
        <div className="label-eyebrow mb-3">Select tee</div>
        <div className="flex flex-wrap gap-2">
          {c.tees.map((t, i) => (
            <button key={t.name} onClick={() => setTeeIdx(i)} data-testid={`tee-pick-${t.name.toLowerCase()}`}
              className={`px-4 py-2 rounded-xl text-sm font-bold border transition ${
                i === teeIdx ? "bg-[#0F2537] text-white border-[#0F2537]" : "bg-white text-[#0F2537] border-[#E2E0D8] hover:border-[#0F2537]"
              }`}>
              <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: t.color, border: t.color === "#F0EFEA" ? "1px solid #ccc" : "none" }} />
              {t.name}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <Stat label="Par" v={c.par} />
        <Stat label="Course Rating" v={tee.course_rating} />
        <Stat label="Slope" v={tee.slope_rating} />
        <button onClick={() => navigate(`/rounds/play/${c.id}?tee=${encodeURIComponent(tee.name)}`)} data-testid="course-play-btn"
          className="rounded-2xl bg-[#E05D3A] text-white p-5 font-bold flex items-center justify-center gap-2 hover:brightness-110 transition">
          <Play weight="fill" size={20} /> Play {tee.name}
        </button>
      </div>

      <Link to={`/courses/${c.id}/flyover`} data-testid="course-flyover-btn"
        className="card-interactive p-5 flex items-center justify-between gap-3 group">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#0F2537] text-white flex items-center justify-center">
            <Compass weight="duotone" size={22} />
          </div>
          <div>
            <span className="label-eyebrow">Pre-round</span>
            <h2 className="font-heading font-bold text-lg mt-0.5">Hole-by-hole satellite flyover</h2>
            <p className="text-xs text-[#0F2537]/60">Satellite view of every hole with distance rings + bag-based club suggestion.</p>
          </div>
        </div>
        <span className="text-[#E05D3A] font-bold text-sm">Open →</span>
      </Link>

      <div className="card-default p-5 md:p-7 overflow-x-auto">
        <span className="label-eyebrow">Scorecard · {tee.name} tees · {tee.total_yards} yards</span>
        <h2 className="font-heading font-bold text-xl mt-1 mb-4">Hole-by-hole</h2>
        <Nine label="Front 9" holes={front} yards={tee.yards.slice(0,9)}
          totals={{ par: sumPar(front), yards: sumYards(0,9) }} />
        <div className="h-3" />
        <Nine label="Back 9" holes={back} yards={tee.yards.slice(9,18)}
          totals={{ par: sumPar(back), yards: sumYards(9,18) }} />
      </div>
      <p className="text-sm text-[#0F2537]/60">
        <Link to="/courses" className="hover:text-[#E05D3A]">← All courses</Link>
      </p>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="card-default p-5">
      <span className="label-eyebrow">{label}</span>
      <div className="font-heading font-black text-2xl mt-1">{v}</div>
    </div>
  );
}

function Nine({ label, holes, yards, totals }) {
  return (
    <div>
      <div className="text-xs font-bold text-[#0F2537]/60 mb-2">{label}</div>
      <table className="w-full text-sm border-separate border-spacing-y-1 min-w-[640px]">
        <thead>
          <tr className="text-[#0F2537]/60 text-xs uppercase tracking-widest">
            <th className="text-left px-2 py-1">Hole</th>
            {holes.map((h) => <th key={h.number} className="px-2 py-1">{h.number}</th>)}
            <th className="px-2 py-1">Tot</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-[#F8F7F3]"><td className="px-2 py-1.5 font-semibold rounded-l-lg">Yards</td>
            {yards.map((y, i) => <td key={i} className="text-center px-2 py-1.5">{y}</td>)}
            <td className="text-center font-bold px-2 py-1.5 rounded-r-lg">{totals.yards}</td>
          </tr>
          <tr><td className="px-2 py-1 font-semibold">Par</td>
            {holes.map((h) => <td key={h.number} className="text-center px-2 py-1">{h.par}</td>)}
            <td className="text-center font-bold px-2 py-1">{totals.par}</td>
          </tr>
          <tr className="bg-[#F8F7F3]"><td className="px-2 py-1.5 font-semibold rounded-l-lg">Hcp</td>
            {holes.map((h) => <td key={h.number} className="text-center px-2 py-1.5">{h.handicap}</td>)}
            <td className="rounded-r-lg" />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
